import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Gift, TicketPercent, Plus, Trash2, Send, Loader2, Copy, Ban, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { api } from '../../App';

const REASONS = ['Sponsor', 'Donor', 'Guest', 'Volunteer', 'Other'];
const fmtDay = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtWhen = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/** Admin-only: issue named free tickets (emailed on click) and manage coupon codes. */
export function ComplimentaryPanel({ token, events = [], selectedEvent = '', onIssued }) {
  const liveEvents = useMemo(() => events.filter(e => e.status !== 'demo'), [events]);
  const [tab, setTab] = useState('issue');
  const [summary, setSummary] = useState(null);
  const [issued, setIssued] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const q = selectedEvent ? `?event_id=${selectedEvent}` : '';
    try {
      const [s, i, c] = await Promise.all([
        api.getAuth(`/api/admin/comps/summary${q}`, token),
        api.getAuth(`/api/admin/comps/issued${q}`, token),
        api.getAuth(`/api/admin/comps/coupons${q}`, token),
      ]);
      setSummary(s); setIssued(i.orders || []); setCoupons(c.coupons || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token, selectedEvent]);
  useEffect(() => { load(); }, [load]);

  const afterChange = () => { load(); if (onIssued) onIssued(); };

  return (
    <Card className="mb-6 border-[hsl(var(--gs-accent-gold))]" data-testid="comps-panel">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-medium flex items-center gap-2"><Gift className="w-4 h-4" /> Complimentary tickets &amp; coupons <Badge variant="outline" className="text-[10px]">Admin only</Badge></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Issue complimentary tickets to sponsors, donors, guests and volunteers. Each ticket carries the standard GS QR, is recorded as <em>Complimentary</em> in lists and exports, and uses a real seat from the event capacity.
            </p>
          </div>
          {summary && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="comps-summary">
              <Badge className="bg-[hsl(var(--gs-accent-gold))/20] text-[hsl(var(--gs-ink))]" data-testid="comps-summary-total">{summary.complimentary_tickets} complimentary</Badge>
              {Object.entries(summary.by_reason || {}).map(([k, v]) => <Badge key={k} variant="secondary">{k}: {v}</Badge>)}
              <Badge variant="outline" data-testid="comps-summary-coupons">{summary.active_coupons} active coupon{summary.active_coupons === 1 ? '' : 's'}</Badge>
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex flex-wrap h-auto justify-start">
            <TabsTrigger value="issue" data-testid="comps-tab-issue"><Send className="w-3.5 h-3.5 mr-1" /> Issue free tickets</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="comps-tab-coupons"><TicketPercent className="w-3.5 h-3.5 mr-1" /> Coupon codes</TabsTrigger>
            <TabsTrigger value="history" data-testid="comps-tab-history">History ({issued.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="issue"><IssueForm token={token} events={liveEvents} defaultEvent={selectedEvent} onDone={afterChange} /></TabsContent>
          <TabsContent value="coupons"><CouponManager token={token} events={liveEvents} defaultEvent={selectedEvent} coupons={coupons} loading={loading} onChange={afterChange} /></TabsContent>
          <TabsContent value="history"><IssuedHistory rows={issued} loading={loading} /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- direct issue
function IssueForm({ token, events, defaultEvent, onDone }) {
  const [eventId, setEventId] = useState(defaultEvent || '');
  useEffect(() => { if (defaultEvent) setEventId(defaultEvent); }, [defaultEvent]);
  const ev = events.find(e => e.event_id === eventId);
  const cats = (ev?.ticket_categories || []).filter(c => c.visible !== false);
  const eventDays = ev?.event_days || [];
  const isMultiDay = eventDays.length > 1;
  const legacyTypes = ev?.ticket_types || {};
  const useLegacy = !!ev && cats.length === 0 && Object.keys(legacyTypes).length > 0;

  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [reason, setReason] = useState('Sponsor');
  const [note, setNote] = useState('');
  const [days, setDays] = useState([]);
  const [season, setSeason] = useState(false);
  const [rows, setRows] = useState([]);
  const [legacyQty, setLegacyQty] = useState({});
  const [sendEmail, setSendEmail] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { setRows([]); setDays([]); setSeason(false); setLegacyQty({}); setResult(null); }, [eventId]);

  const addRow = () => {
    if (!cats.length) return;
    setRows(r => [...r, { _uiId: crypto.randomUUID(), category_id: cats[0].id, name: r.length === 0 ? recipientName : '', gender: '' }]);
  };
  const updateRow = (i, patch) => setRows(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const toggleDay = (d) => { setSeason(false); setDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d].sort()); };

  const qrCount = useLegacy
    ? Object.values(legacyQty).reduce((s, q) => s + (Number(q) || 0), 0)
    : rows.reduce((n, r) => { const c = cats.find(x => x.id === r.category_id); if (c?.fixed_day) return n + 1; return n + (season ? eventDays.length : (isMultiDay ? days.length : 1)); }, 0);
  const remaining = ev ? (ev.capacity || 0) - (ev.tickets_sold || 0) : 0;

  const submit = async () => {
    if (!eventId) { toast.error('Choose an event'); return; }
    if (!recipientName.trim() || !recipientEmail.trim()) { toast.error('Enter the recipient name and email'); return; }
    if (!useLegacy && rows.length === 0) { toast.error('Add at least one ticket'); return; }
    if (!useLegacy && rows.some(r => !r.name.trim())) { toast.error('Enter a name for every ticket'); return; }
    if (!useLegacy && isMultiDay && !season && days.length === 0) { toast.error('Select the day(s) or a season pass'); return; }
    setBusy(true);
    try {
      const body = { event_id: eventId, recipient_name: recipientName.trim(), recipient_email: recipientEmail.trim(), reason, note, send_email: sendEmail, coupon_code: couponCode.trim() || null,
        days: season ? [] : (isMultiDay ? days : eventDays), season,
        tickets: useLegacy ? [] : rows.map(r => ({ category_id: r.category_id, name: r.name.trim(), gender: r.gender })),
        items: useLegacy ? Object.entries(legacyQty).filter(([, q]) => Number(q) > 0).map(([type, quantity]) => ({ type, quantity: Number(quantity) })) : [] };
      const res = await api.postAuth('/api/admin/comps/issue', body, token);
      toast.success(res.message);
      setResult(res);
      setRows([]); setLegacyQty({}); setNote('');
      onDone();
    } catch (e) { toast.error(e.message, { duration: 8000 }); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="comps-issue-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Event</Label>
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger data-testid="comps-issue-event"><SelectValue placeholder="Choose an event" /></SelectTrigger>
            <SelectContent>{events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}</SelectContent>
          </Select>
          {ev && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1" data-testid="comps-issue-remaining">{remaining} of {ev.capacity} seats remaining</p>}
        </div>
        <div>
          <Label className="text-xs">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="comps-issue-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Recipient name (booking contact)</Label>
          <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="e.g. Mr Patel (Gold Sponsor)" data-testid="comps-issue-name" />
        </div>
        <div>
          <Label className="text-xs">Recipient email (tickets are sent here)</Label>
          <Input type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="name@example.com" data-testid="comps-issue-email" />
        </div>
      </div>

      {ev && !useLegacy && isMultiDay && (
        <div>
          <Label className="text-xs">Day(s)</Label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="comps-issue-days">
            {eventDays.map(d => (
              <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${!season && days.includes(d) ? 'bg-[hsl(var(--primary))] text-white border-transparent' : 'hover:bg-[hsl(var(--accent))]'}`} data-testid={`comps-issue-day-${d}`}>{fmtDay(d)}</button>
            ))}
            {cats.some(c => Number(c.season_price || 0) > 0) && (
              <button type="button" onClick={() => { setSeason(s => !s); setDays([]); }} className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${season ? 'bg-[hsl(var(--gs-navy))] text-white border-transparent' : 'hover:bg-[hsl(var(--accent))]'}`} data-testid="comps-issue-season">Season (all days)</button>
            )}
          </div>
        </div>
      )}

      {ev && !useLegacy && (
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Tickets ({rows.length} {rows.length === 1 ? 'person' : 'people'} · {qrCount} QR{qrCount === 1 ? '' : 's'})</Label>
            <Button type="button" size="sm" variant="outline" onClick={addRow} disabled={!cats.length} data-testid="comps-issue-add-ticket"><Plus className="w-3.5 h-3.5 mr-1" /> Add ticket</Button>
          </div>
          <div className="space-y-2 mt-2">
            {rows.map((r, i) => (
              <div key={stableRecordKey(r)} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px_36px] gap-2 items-center" data-testid={`comps-issue-row-${i}`}>
                <Input value={r.name} onChange={e => updateRow(i, { name: e.target.value })} placeholder="Attendee full name" aria-label="Attendee name" data-testid={`comps-issue-row-name-${i}`} />
                <Select value={r.category_id} onValueChange={v => updateRow(i, { category_id: v })}>
                  <SelectTrigger aria-label="Ticket category" data-testid={`comps-issue-row-cat-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.fixed_day ? ` (${fmtDay(c.fixed_day)} only)` : ''}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={r.gender || 'na'} onValueChange={v => updateRow(i, { gender: v === 'na' ? '' : v })}>
                  <SelectTrigger aria-label="Gender" data-testid={`comps-issue-row-gender-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="na">Gender —</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem></SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(i)} aria-label="Remove ticket" data-testid={`comps-issue-row-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {rows.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="comps-issue-no-rows">No tickets added yet — click <em>Add ticket</em> for each named person.</p>}
          </div>
        </div>
      )}

      {ev && useLegacy && (
        <div>
          <Label className="text-xs">Tickets ({qrCount} QR{qrCount === 1 ? '' : 's'})</Label>
          <div className="grid sm:grid-cols-3 gap-2 mt-1">
            {Object.entries(legacyTypes).map(([type, info]) => (
              <div key={type} className="flex items-center gap-2 rounded-lg border p-2">
                <span className="text-sm flex-1 capitalize">{info.label || type.replace('_', ' ')}</span>
                <Input type="number" min={0} max={50} className="w-20 h-8" value={legacyQty[type] || ''} onChange={e => setLegacyQty(q => ({ ...q, [type]: e.target.value }))} aria-label={`${type} quantity`} data-testid={`comps-issue-qty-${type}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-[1fr_220px] gap-3">
        <div>
          <Label className="text-xs">Internal note (optional, staff only)</Label>
          <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Gold sponsor 2025 – agreed by committee" data-testid="comps-issue-note" />
        </div>
        <div>
          <Label className="text-xs">Redeem a coupon code (optional)</Label>
          <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. GS-ABC123" className="font-mono" data-testid="comps-issue-coupon" />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Records the issue against the coupon's usage.</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={sendEmail} onCheckedChange={v => setSendEmail(!!v)} data-testid="comps-issue-send-email" /> Email the tickets to the recipient now
        </label>
        <div className="flex-1" />
        <Button onClick={submit} disabled={busy || !eventId || qrCount === 0} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="comps-issue-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
          {busy ? 'Issuing…' : `Issue ${qrCount || ''} free ticket${qrCount === 1 ? '' : 's'}${sendEmail ? ' & email' : ''}`}
        </Button>
      </div>
      {result && (
        <div className="rounded-lg border border-[hsl(var(--gs-accent-gold))] bg-[hsl(var(--gs-accent-gold))/10] p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2" data-testid="comps-issue-result">
          <CheckCircle2 className="w-4 h-4 text-green-700 shrink-0" />
          <span className="flex-1">{result.message}. Order <span className="font-mono text-xs">{result.order_id}</span>{result.queued ? ' · email queued (sends when email delivery is connected)' : ''}</span>
          <a href={`/payment/success?order_id=${result.order_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs underline" data-testid="comps-issue-view-passes"><ExternalLink className="w-3 h-3 mr-1" /> View passes</a>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- coupons
function CouponManager({ token, events, defaultEvent, coupons, loading, onChange }) {
  const [form, setForm] = useState({ code: '', event_id: defaultEvent || '', kind: 'free', value: '', max_uses: 1, max_tickets: 1, expires_at: '', category_ids: [], reason: 'Guest', recipient_name: '', note: '' });
  useEffect(() => { if (defaultEvent) setForm(f => ({ ...f, event_id: defaultEvent })); }, [defaultEvent]);
  const [busy, setBusy] = useState(false);
  const ev = events.find(e => e.event_id === form.event_id);
  const cats = (ev?.ticket_categories || []).filter(c => c.visible !== false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const toggleCat = (id) => set({ category_ids: form.category_ids.includes(id) ? form.category_ids.filter(x => x !== id) : [...form.category_ids, id] });

  const create = async () => {
    if (form.kind !== 'free' && !(Number(form.value) > 0)) { toast.error(form.kind === 'percent' ? 'Enter the percentage off' : 'Enter the £ amount off'); return; }
    setBusy(true);
    try {
      const res = await api.postAuth('/api/admin/comps/coupons', {
        code: form.code || null, event_id: form.event_id || null, kind: form.kind, value: Number(form.value) || 0,
        max_uses: Number(form.max_uses) || 0, max_tickets: Number(form.max_tickets) || 0, expires_at: form.expires_at || null,
        category_ids: form.event_id ? form.category_ids : [], reason: form.reason, recipient_name: form.recipient_name, note: form.note,
      }, token);
      toast.success(res.message);
      setForm(f => ({ ...f, code: '', value: '', recipient_name: '', note: '', category_ids: [] }));
      onChange();
    } catch (e) { toast.error(e.message, { duration: 8000 }); }
    setBusy(false);
  };
  const setStatus = async (c, status) => {
    try { const r = await api.putAuth(`/api/admin/comps/coupons/${c.coupon_id}`, { status }, token); toast.success(r.message); onChange(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? (Only possible if it has never been redeemed.)`)) return;
    try { const r = await api.deleteAuth(`/api/admin/comps/coupons/${c.coupon_id}`, token); toast.success(r.message); onChange(); } catch (e) { toast.error(e.message); }
  };
  const copy = async (code) => { try { await navigator.clipboard.writeText(code); toast.success(`${code} copied`); } catch { toast.info(code); } };

  return (
    <div className="space-y-5" data-testid="comps-coupons">
      <div className="rounded-xl border p-3 sm:p-4 space-y-3" data-testid="comps-coupon-form">
        <div className="text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> New coupon code</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Code (blank = auto)</Label>
            <Input value={form.code} onChange={e => set({ code: e.target.value.toUpperCase() })} placeholder="e.g. SPONSOR2025" data-testid="comps-coupon-code" />
          </div>
          <div>
            <Label className="text-xs">Event</Label>
            <Select value={form.event_id || 'any'} onValueChange={v => set({ event_id: v === 'any' ? '' : v, category_ids: [] })}>
              <SelectTrigger data-testid="comps-coupon-event"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any event</SelectItem>{events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.kind} onValueChange={v => set({ kind: v })}>
              <SelectTrigger data-testid="comps-coupon-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="free">100% free tickets</SelectItem><SelectItem value="percent">Percentage off</SelectItem><SelectItem value="amount">£ amount off</SelectItem></SelectContent>
            </Select>
          </div>
          {form.kind === 'free' ? (
            <div>
              <Label className="text-xs">Free tickets per use (0 = whole booking)</Label>
              <Input type="number" min={0} max={100} value={form.max_tickets} onChange={e => set({ max_tickets: e.target.value })} data-testid="comps-coupon-max-tickets" />
            </div>
          ) : (
            <div>
              <Label className="text-xs">{form.kind === 'percent' ? 'Percentage off (1–100)' : 'Amount off (£)'}</Label>
              <Input type="number" min={0} step={form.kind === 'percent' ? 1 : 0.5} value={form.value} onChange={e => set({ value: e.target.value })} placeholder={form.kind === 'percent' ? '50' : '5.00'} data-testid="comps-coupon-value" />
            </div>
          )}
          <div>
            <Label className="text-xs">Max uses (0 = unlimited)</Label>
            <Input type="number" min={0} max={10000} value={form.max_uses} onChange={e => set({ max_uses: e.target.value })} data-testid="comps-coupon-max-uses" />
          </div>
          <div>
            <Label className="text-xs">Expires (optional)</Label>
            <Input type="date" value={form.expires_at} onChange={e => set({ expires_at: e.target.value })} data-testid="comps-coupon-expires" />
          </div>
          <div>
            <Label className="text-xs">Reason / tag</Label>
            <Select value={form.reason} onValueChange={v => set({ reason: v })}>
              <SelectTrigger data-testid="comps-coupon-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">For (name, optional)</Label>
            <Input value={form.recipient_name} onChange={e => set({ recipient_name: e.target.value })} placeholder="e.g. ABC Ltd" data-testid="comps-coupon-recipient" />
          </div>
        </div>
        {ev && cats.length > 0 && (
          <div>
            <Label className="text-xs">Limit to ticket categories (optional)</Label>
            <div className="flex flex-wrap gap-3 mt-1" data-testid="comps-coupon-categories">
              {cats.map(c => (
                <label key={c.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox checked={form.category_ids.includes(c.id)} onCheckedChange={() => toggleCat(c.id)} data-testid={`comps-coupon-cat-${c.id}`} /> {c.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs">Internal note (optional)</Label>
            <Input value={form.note} onChange={e => set({ note: e.target.value })} placeholder="staff only" data-testid="comps-coupon-note" />
          </div>
          <Button onClick={create} disabled={busy} data-testid="comps-coupon-create">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TicketPercent className="w-4 h-4 mr-1" />} Create coupon</Button>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Coupon codes are held by staff and are not shown on the public booking page. To honour a code, issue the tickets from the <em>Issue free tickets</em> tab and enter the code there — the redemption is recorded against the coupon's usage.</p>
      </div>

      <div className="overflow-x-auto" data-testid="comps-coupon-list">
        {loading ? <div className="animate-pulse h-16 bg-[hsl(var(--muted))] rounded-lg" /> : coupons.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] p-3" data-testid="comps-coupon-empty">No coupon codes yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-[hsl(var(--muted-foreground))]">
              <th className="p-2 font-medium">Code</th><th className="p-2 font-medium">Offer</th><th className="p-2 font-medium">Event</th><th className="p-2 font-medium">Uses</th><th className="p-2 font-medium">Expires</th><th className="p-2 font-medium">Status</th><th className="p-2 font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {coupons.map(c => {
                const dead = c.status !== 'active' || c.expired || c.exhausted;
                return (
                  <tr key={c.coupon_id} className={`border-b ${dead ? 'opacity-60' : ''}`} data-testid={`comps-coupon-row-${c.code}`}>
                    <td className="p-2 font-mono text-xs whitespace-nowrap">
                      <button type="button" onClick={() => copy(c.code)} className="inline-flex items-center gap-1 hover:underline" title="Copy code" data-testid={`comps-coupon-copy-${c.code}`}>{c.code} <Copy className="w-3 h-3" /></button>
                      {c.recipient_name && <div className="text-[10px] text-[hsl(var(--muted-foreground))] font-sans">{c.recipient_name}</div>}
                    </td>
                    <td className="p-2">{c.description}<div className="text-[10px] text-[hsl(var(--muted-foreground))]">{c.reason}{c.category_ids?.length ? ` · ${c.category_ids.join(', ')}` : ''}</div></td>
                    <td className="p-2 text-xs">{c.event_title || 'Any event'}</td>
                    <td className="p-2 text-xs whitespace-nowrap" data-testid={`comps-coupon-uses-${c.code}`}>{c.uses}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}{c.tickets_redeemed ? <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{c.tickets_redeemed} ticket{c.tickets_redeemed === 1 ? '' : 's'}</div> : null}</td>
                    <td className="p-2 text-xs">{c.expires_at || '—'}</td>
                    <td className="p-2"><Badge variant={dead ? 'secondary' : 'default'} className="text-[10px]">{c.status !== 'active' ? 'Disabled' : c.expired ? 'Expired' : c.exhausted ? 'Used up' : 'Active'}</Badge></td>
                    <td className="p-2 whitespace-nowrap">
                      {c.status === 'active'
                        ? <Button size="sm" variant="ghost" onClick={() => setStatus(c, 'disabled')} title="Disable" data-testid={`comps-coupon-disable-${c.code}`}><Ban className="w-3.5 h-3.5" /></Button>
                        : <Button size="sm" variant="ghost" onClick={() => setStatus(c, 'active')} title="Re-enable" data-testid={`comps-coupon-enable-${c.code}`}><RefreshCw className="w-3.5 h-3.5" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => remove(c)} title="Delete (never redeemed only)" data-testid={`comps-coupon-delete-${c.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- history
function IssuedHistory({ rows, loading }) {
  if (loading) return <div className="animate-pulse h-16 bg-[hsl(var(--muted))] rounded-lg" />;
  if (!rows.length) return <p className="text-sm text-[hsl(var(--muted-foreground))] p-3" data-testid="comps-history-empty">No complimentary or coupon bookings yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="comps-history">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-[hsl(var(--muted-foreground))]">
          <th className="p-2 font-medium">When</th><th className="p-2 font-medium">Recipient</th><th className="p-2 font-medium">Event</th><th className="p-2 font-medium">Tickets</th><th className="p-2 font-medium">Tag</th><th className="p-2 font-medium">Paid</th><th className="p-2 font-medium">By</th><th className="p-2 font-medium"></th>
        </tr></thead>
        <tbody>
          {rows.map(o => (
            <tr key={o.order_id} className="border-b" data-testid={`comps-history-row-${o.order_id}`}>
              <td className="p-2 text-xs whitespace-nowrap">{fmtWhen(o.created_at)}</td>
              <td className="p-2">{o.customer_name}<div className="text-xs text-[hsl(var(--muted-foreground))]">{o.customer_email}</div>{o.note && <div className="text-[10px] italic text-[hsl(var(--muted-foreground))]">{o.note}</div>}</td>
              <td className="p-2 text-xs">{o.event_title}</td>
              <td className="p-2 tabular-nums">{o.tickets}</td>
              <td className="p-2">
                {o.complimentary ? <Badge className="bg-[hsl(var(--gs-accent-gold))/20] text-[hsl(var(--gs-ink))] text-[10px]">Complimentary{o.reason ? ` · ${o.reason}` : ''}</Badge> : <Badge variant="outline" className="text-[10px]">Discount</Badge>}
                {o.coupon_code && <div className="font-mono text-[10px] mt-0.5">{o.coupon_code}</div>}
              </td>
              <td className="p-2 tabular-nums text-xs">£{Number(o.total_amount || 0).toFixed(2)}{o.discount_amount > 0 && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">−£{Number(o.discount_amount).toFixed(2)}</div>}</td>
              <td className="p-2 text-xs">{o.issued_by ? o.issued_by.split('@')[0] : 'coupon'}</td>
              <td className="p-2"><a href={`/payment/success?order_id=${o.order_id}`} target="_blank" rel="noreferrer" className="text-xs underline inline-flex items-center" data-testid={`comps-history-view-${o.order_id}`}><ExternalLink className="w-3 h-3 mr-1" /> Passes</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
